import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useFormatters } from '../../lib/format'
import { EmptyState } from '../ui/empty-state'

/**
 * What is behind a value: the contracts and interventions its spend carries,
 * heaviest first. The server filters them by the caller's own permissions, and
 * says when some were withheld.
 */
interface Props {
    org: string
    subjectUid: string
    metric: string
    breakdownKey: string
    windowEnd?: string
    currency: string
}

export function BenchmarkSources({ org, subjectUid, metric, breakdownKey, windowEnd, currency }: Props) {
    const { t } = useTranslation('benchmark')
    const { formatMoney } = useFormatters()
    const query = $api.useQuery('get', '/api/v1/organizations/{orgCode}/benchmark/assets/{assetUid}/sources', {
        params: {
            path: { orgCode: org, assetUid: subjectUid },
            query: { metric, breakdown_key: breakdownKey || undefined, window_end: windowEnd },
        },
    })
    const data = query.data
    if (query.isLoading) return <div className="h-16 animate-pulse rounded-md bg-muted" />
    if (!data) return null

    const empty = data.contracts.length === 0 && data.interventions.length === 0

    return (
        <div className="space-y-3" data-testid="benchmark-sources">
            {empty && !data.withheld && <EmptyState variant="inline" title={t('sources.empty')} />}
            {data.contracts.length > 0 && (
                <div>
                    <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('sources.contracts')}</h4>
                    <ul className="divide-y rounded-md border">
                        {data.contracts.map((c) => (
                            <li key={c.uid} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                <Link
                                    to="/$org/contracts/$contractUid"
                                    params={{ org, contractUid: c.uid }}
                                    className="min-w-0 truncate text-foreground hover:underline"
                                    data-testid={`benchmark-source-contract-${c.code}`}
                                >
                                    {c.name || c.code}
                                </Link>
                                <span className="shrink-0 tabular-nums text-muted-foreground">{formatMoney(c.amount, currency)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {data.interventions.length > 0 && (
                <div>
                    <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('sources.interventions')}</h4>
                    <ul className="divide-y rounded-md border">
                        {data.interventions.map((i) => (
                            <li key={i.uid} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                <Link
                                    to="/$org/interventions/$interventionUid"
                                    params={{ org, interventionUid: i.uid }}
                                    className="min-w-0 truncate text-foreground hover:underline"
                                    data-testid={`benchmark-source-intervention-${i.uid}`}
                                >
                                    {i.reference ? `${i.reference} · ${i.title}` : i.title}
                                </Link>
                                <span className="shrink-0 tabular-nums text-muted-foreground">{formatMoney(i.amount, currency)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {data.withheld && <p className="text-xs text-muted-foreground">{t('sources.withheld')}</p>}
        </div>
    )
}
